import { cx } from "@emotion/css";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BlockedActions } from "src/components/Course/lessons/Lesson/LessonComponents/Actions/BlockedActions";
import { InProgressActions } from "src/components/Course/lessons/Lesson/LessonComponents/Actions/InProgressActions";
import { WaitingActions } from "src/components/Course/lessons/Lesson/LessonComponents/Actions/WaitingActions";
import { BlockedRepetitionDetails } from "src/components/Course/lessons/Lesson/LessonComponents/Details/BlockedRepetitionDetails";
import InProgressDetails from "src/components/Course/lessons/Lesson/LessonComponents/Details/InProgressDetails";
import { WaitingRepetitionDetails } from "src/components/Course/lessons/Lesson/LessonComponents/Details/WaitingRepetitionDetails";
import { LessonNote } from "src/components/Course/lessons/Lesson/LessonNote";
import { DefaultScore } from "src/components/NewComponents/Views/Details/DefaultDetails";
import DefaultScoreView from "src/components/NewComponents/Views/ScoreViews/DefaultScoreView";

export type LessonStatus = "resolved" | "waiting" | "incomplete" | "blocked";

export interface LessonExam {
	id: number;
	status: string;
	score: number;
}

// Powtórka/wyzwanie trzymają w itemsIds[0] dni do rozwiązania, pozostałe lekcje — id pytań
type LessonItem = number | { lessonDays: number[] };

export interface CourseLesson {
	id: number;
	courseId: number;
	name: string | null;
	type: string;
	status: LessonStatus;
	day: number;
	startDate: string | null;
	itemId: number | null;
	itemsIds: LessonItem[];
	userNote?: string | null;
	exam: LessonExam | null;
}

export interface LessonFolder {
	id: string;
	name: string;
	details?: { color?: string | null } | null;
}

export const getLessonDaysCount = (
	lesson: CourseLesson,
): number | undefined => {
	const [first] = lesson.itemsIds;
	return first && typeof first === "object"
		? first.lessonDays.length
		: undefined;
};

export const isLessonDisabled = (lesson: CourseLesson): boolean => {
	return lesson.status === "blocked";
};

export const isLessonWaiting = (lesson: CourseLesson): boolean => {
	return lesson.status === "waiting";
};

export const isLessonResolved = (lesson: CourseLesson): boolean => {
	return lesson.status === "resolved";
};

interface RepetitionLessonProps {
	lesson: CourseLesson;
	className?: string;
	folders?: LessonFolder[];
}

export const RepetitionLesson = ({
	lesson,
	className,
	folders = [],
}: RepetitionLessonProps) => {
	const { t } = useTranslation();
	const [isNoteOpen, setIsNoteOpen] = useState(!!lesson.userNote);
	const toggleNote = () => setIsNoteOpen(!isNoteOpen);
	const isWaiting = isLessonWaiting(lesson);
	const isBlocked = isLessonDisabled(lesson);

	if (isBlocked) {
		return (
			<DefaultScoreView
				className={cx("disabled-lesson", className)}
				Details={<BlockedRepetitionDetails lesson={lesson} className="mr-32" />}
				Actions={<BlockedActions lesson={lesson} />}
			/>
		);
	}

	if (isWaiting) {
		return (
			<DefaultScoreView
				className={cx(
					{
						"disabled-lesson": isLessonDisabled(lesson),
					},
					className,
				)}
				Details={<WaitingRepetitionDetails className="mr-32" lesson={lesson} />}
				Actions={<WaitingActions lesson={lesson} toggleNote={toggleNote} />}
			>
				{isNoteOpen && (
					<LessonNote
						lesson={lesson}
						closeNotes={() => setIsNoteOpen(false)}
						folders={folders}
					/>
				)}
			</DefaultScoreView>
		);
	}

	return (
		<DefaultScoreView
			className={className}
			Details={
				<InProgressDetails
					className="mr-32"
					lesson={lesson}
					Score={
						<DefaultScore
							className="purple-box"
							title={t("course.lessons.revision")}
						/>
					}
				/>
			}
			Actions={<InProgressActions lesson={lesson} toggleNote={toggleNote} />}
		>
			{isNoteOpen && (
				<LessonNote
					lesson={lesson}
					closeNotes={() => setIsNoteOpen(false)}
					folders={folders}
				/>
			)}
		</DefaultScoreView>
	);
};
